
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChannelType, Period } from '../types';
import { 
  Check, 
  RefreshCw, 
  ArrowUpRight, 
  ArrowRight,
  Shuffle, 
  TrendingUp, 
  Archive, 
  Mail, 
  Zap, 
  ArrowDownLeft, 
  Settings,
  Server,
  Activity,
  FileText,
  AlertCircle,
  X,
  PlayCircle
} from '../components/Icons';
import FlowDiagram from '../components/FlowDiagram';
import { Card, Button, PageHeader } from '../components/UI';
import { BrandLogo } from '../components/BrandLogo';
import { TRANSITIONS } from '../constants/theme';
import { useTranslation } from '../contexts/LanguageContext';

import { transactionData } from '../data/transactions';
import { usageData } from '../data/usage';
import { topologyData } from '../data/topology';

// Reusable Tile for Outbound Channels (Right Side)
const OutboundTile: React.FC<{ type: string; isActive?: boolean; isInternal?: boolean }> = ({ type, isActive, isInternal }) => {
  const navigate = useNavigate();
  
  const handleNavigation = () => {
    // Map types to settings pages
    const pathMap: Record<string, string> = {
      [ChannelType.EBILL]: '/settings/channels/ebill',
      [ChannelType.EDI]: '/settings/channels/edi',
      [ChannelType.EMAIL]: '/settings/channels/email',
      [ChannelType.PRINT]: '/settings/channels/print',
      'SFTP': '/settings/inbound/sftp', // Reusing SFTP config for outbound for now
    };
    navigate(pathMap[type] || '/settings/channels');
  };

  const getIcon = () => {
    // Using lighter olive for dark mode contrast
    if (type === 'SFTP') return <Archive size={28} className="text-[#474D22] dark:text-[#B5BF50]" />;
    return <BrandLogo type={type as ChannelType} size={28} />;
  };

  return (
    <button 
      onClick={handleNavigation}
      className={`flex items-center justify-between px-4 py-3 border ${TRANSITIONS} w-full group relative rounded-[6px] ${
        isActive 
          ? 'bg-[#F5F7FA] dark:bg-[#262626] border-gray-200 dark:border-gray-700 hover:border-[#474D22] shadow-sm' 
          : 'border-dashed border-amber-200 dark:border-amber-900/50 bg-amber-50/30 dark:bg-amber-950/10 hover:border-amber-300 dark:hover:border-amber-700'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={isActive ? "" : "grayscale opacity-80"}>
          {getIcon()}
        </div>
        <div className="flex flex-col items-start">
          <span className={`text-xs font-bold tracking-wide uppercase ${isActive ? 'text-[#161616] dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
            {type}
          </span>
          <span className={`text-[9px] font-medium ${isActive ? 'text-gray-400' : 'text-amber-600 dark:text-amber-500'}`}>
            {isActive ? (isInternal ? 'Internal Route' : 'Active') : 'Not Configured'}
          </span>
        </div>
      </div>
      
      {isActive ? (
        <div className="w-5 h-5 rounded-full bg-[#474D22]/10 dark:bg-[#474D22]/30 flex items-center justify-center">
           <Check size={12} className="text-[#474D22] dark:text-[#B5BF50]" strokeWidth={4} />
        </div>
      ) : (
        <div className="w-5 h-5 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center animate-pulse">
           <Settings size={12} className="text-amber-600 dark:text-amber-500" />
        </div>
      )}
    </button>
  );
};

// Reusable Tile for Inbound Channels (Left Side)
const InboundTile: React.FC<{ type: string; path?: string; isActive?: boolean }> = ({ type, path, isActive }) => {
  const navigate = useNavigate();
  
  const getIcon = () => {
      // Consistent color with FlowDiagram nodes: Brand Medium in Light, Lighter Olive in Dark
      const activeClass = isActive ? "text-[#474D22] dark:text-[#B5BF50]" : "text-gray-400";
      switch(type) {
          case 'SFTP': return <Archive size={16} className={activeClass} />;
          case 'eMail': return <Mail size={16} className={activeClass} />;
          case 'API': return <Zap size={16} className={activeClass} />;
          case 'EDI': return <Zap size={16} className={activeClass} />;
          default: return <ArrowDownLeft size={16} className={activeClass} />;
      }
  };

  return (
    <button 
      onClick={() => path && navigate(path)}
      className={`flex items-center justify-between px-4 py-3 border ${TRANSITIONS} w-full group relative rounded-[6px] ${
        isActive 
          ? 'bg-[#F5F7FA] dark:bg-[#262626] border-gray-200 dark:border-gray-700 hover:border-[#474D22] shadow-sm' 
          : 'border-dashed border-amber-200 dark:border-amber-900/50 bg-amber-50/30 dark:bg-amber-950/10 hover:border-amber-300 dark:hover:border-amber-700'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-7 h-7 flex items-center justify-center rounded-full ${isActive ? 'bg-white dark:bg-[#353535]' : 'bg-gray-100 dark:bg-white/5'}`}>
          {getIcon()}
        </div>
        <div className="flex flex-col items-start">
          <span className={`text-xs font-bold tracking-wide uppercase ${isActive ? 'text-[#161616] dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
            {type}
          </span>
          <span className={`text-[9px] font-medium ${isActive ? 'text-gray-400' : 'text-amber-600 dark:text-amber-500'}`}>
            {isActive ? 'Receiving' : 'Setup Required'}
          </span>
        </div>
      </div>
      {isActive ? (
        <div className="w-5 h-5 rounded-full bg-[#474D22]/10 dark:bg-[#474D22]/30 flex items-center justify-center">
           <Check size={12} className="text-[#474D22] dark:text-[#B5BF50]" strokeWidth={4} />
        </div>
      ) : (
        <div className="w-5 h-5 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center animate-pulse">
           <AlertCircle size={12} className="text-amber-600 dark:text-amber-500" />
        </div>
      )}
    </button>
  );
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedPeriod, setSelectedPeriod] = useState<Period | 'Custom'>('Day');
  const [company, setCompany] = useState('OctoSuite Enterprise');
  const [status, setStatus] = useState('All');
  const [dateRange, setDateRange] = useState('Last 30 Days');
  const [showWelcome, setShowWelcome] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('new') === 'true') {
      setShowWelcome(true);
    }
  }, [location]);

  const usagePercent = (usageData.txe.used / usageData.txe.total) * 100;

  // --- Configuration Data Maps ---
  
  // Debtors: Sending Invoices (Source -> Channel)
  const debtorConfig = {
    inbound: [
      { type: 'SFTP', isActive: true, path: '/settings/inbound/sftp' },
      { type: 'eMail', isActive: true, path: '/settings/inbound/email' },
      { type: 'API', isActive: true, path: '/settings/inbound/api' }
    ],
    outbound: [
      { type: ChannelType.EBILL, isActive: true },
      { type: ChannelType.EDI, isActive: true },
      { type: ChannelType.EMAIL, isActive: true },
      { type: ChannelType.PRINT, isActive: true }
    ],
    centerLabel: 'Processing',
    inboundLabel: 'Data Intake (ERP)',
    outboundLabel: 'Distribution (Customer)'
  };

  const currentConfig = debtorConfig;

  // --- Chart Logic ---

  const filteredTransactions = useMemo(() => {
    return transactionData.transactions.filter(t => {
      const matchesStatus = status === 'All' || t.status === status;
      // Force 'Send' direction for Debtors only view
      const matchesDirection = t.direction === 'Send';
      return matchesStatus && matchesDirection;
    });
  }, [status]);

  const reactiveTopology = useMemo(() => {
    const nodes = [...topologyData.nodes];
    return nodes.map((node) => {
      const baseVal = parseFloat(node.value.replace('.', ''));
      const ledgerMultiplier = 1.0;
      const filterRatio = filteredTransactions.length / transactionData.transactions.length;
      const finalVal = Math.max(1, Math.floor(baseVal * ledgerMultiplier * (filterRatio + 0.3)));
      return { ...node, value: finalVal.toLocaleString().replace(',', '.') };
    });
  }, [status, filteredTransactions]);

  const primaryIngestionValue = useMemo(() => {
    const sum = reactiveTopology.reduce((acc, node) => {
      const numericVal = parseFloat(node.value.replace(/\./g, ''));
      return acc + numericVal;
    }, 0);
    return sum.toLocaleString().replace(',', '.');
  }, [reactiveTopology]);

  return (
    <div className="p-6 max-w-[1920px] mx-auto space-y-6">
      
      {showWelcome && (
        <div className="animate-in slide-in-from-top-4 duration-500 mb-6">
          <Card className="p-6 bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800 flex items-center justify-between shadow-lg">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-600 flex items-center justify-center text-white shadow-md">
                 <Check size={24} strokeWidth={3} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-green-800 dark:text-green-300">Successfully completed onboarding</h3>
                <p className="text-sm text-green-700 dark:text-green-400 font-medium">Your multichannel gateway is live and configured for {company}.</p>
              </div>
            </div>
            <button onClick={() => setShowWelcome(false)} className="text-green-700 dark:text-green-400 hover:scale-110 transition-transform">
               <X size={20} />
            </button>
          </Card>
        </div>
      )}

      <PageHeader 
        title={t('gateway_dashboard')} 
        breadcrumbs={[{ label: t('main') }, { label: t('dashboard') }]}
      />

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-stretch">
        {/* Main Gateway Visualization Card */}
        <div className="xl:col-span-9 flex flex-col">
          <Card overflowHidden className="flex flex-col flex-grow bg-white dark:bg-[#161616] p-0 border-t-4 border-t-[#474D22]">
            
            {/* Header */}
            <div className="px-8 py-6 border-b border-gray-100 dark:border-[#393939] flex flex-col sm:flex-row justify-between items-center gap-4">
               <div className="space-y-1">
                  <div className="flex items-center gap-3">
                     <h2 className="text-xl font-bold text-[#161616] dark:text-white uppercase tracking-tight">{t('multichannel_gateway')}</h2>
                     <span className="px-2 py-0.5 rounded text-[9px] font-black bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400 uppercase tracking-widest flex items-center gap-1">
                        {/* <Activity size={12} /> Operational */}
                     </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Configure document flow from intake to distribution.</p>
               </div>

               <button 
                  onClick={() => navigate('/onboarding?step=intake&skipProfile=true')}
                  className="flex items-center gap-2 px-5 py-2.5 bg-[#474D22] hover:bg-[#3A3F1C] text-white rounded-[6px] transition-all shadow-sm hover:shadow-md group"
               >
                  <PlayCircle size={16} className="group-hover:scale-110 transition-transform" />
                  <span className="text-[10px] font-bold uppercase tracking-widest">Setup All</span>
               </button>
            </div>

            <div className="flex flex-col lg:flex-row h-full relative">
               
               {/* Zone 1: Inbound (Left) */}
               <div className="flex-1 p-8 bg-gray-50/50 dark:bg-[#1A1A1A]">
                  <div className="flex items-center gap-3 mb-6">
                     <div className="w-8 h-8 rounded-full bg-[#474D22] text-white flex items-center justify-center font-bold text-sm shadow-md">1</div>
                     <div>
                        <h3 className="text-sm font-bold text-[#161616] dark:text-white uppercase tracking-wider">{currentConfig.inboundLabel}</h3>
                        <p className="text-[10px] text-gray-500 dark:text-gray-400 font-medium uppercase tracking-widest">Sources</p>
                     </div>
                  </div>
                  <div className="grid grid-cols-1 gap-3 relative z-10">
                     {currentConfig.inbound.map((conn) => (
                        <InboundTile key={conn.type} type={conn.type} path={conn.path} isActive={conn.isActive} />
                     ))}
                  </div>
               </div>

               {/* Zone 2: Processing (Center) */}
               <div className="relative w-full lg:w-32 flex items-center justify-center py-6 lg:py-0 border-t lg:border-t-0 lg:border-l lg:border-r border-gray-100 dark:border-[#393939] z-20 bg-white dark:bg-[#161616]">
                  {/* Decorative Background Line */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                     <div className="h-full w-px bg-gray-100 dark:bg-[#393939] hidden lg:block"></div>
                     <div className="w-full h-px bg-gray-100 dark:bg-[#393939] lg:hidden"></div>
                  </div>

                  <div className="relative z-10 flex flex-col items-center gap-2">
                     <div className="w-16 h-16 bg-[#F5F7FA] dark:bg-[#262626] border-2 rounded-full flex items-center justify-center shadow-lg group cursor-pointer hover:scale-105 transition-transform border-[#474D22] dark:border-[#B5BF50]">
                        <Shuffle size={24} className="text-[#474D22] dark:text-[#B5BF50] group-hover:animate-spin-slow" />
                     </div>
                     <span className="text-[9px] font-black uppercase tracking-widest bg-white dark:bg-[#161616] px-2 text-[#474D22] dark:text-[#B5BF50]">
                       {currentConfig.centerLabel}
                     </span>
                  </div>

                  {/* Flow Arrows */}
                  <div className="absolute top-1/2 left-0 -translate-x-1/2 -translate-y-1/2 z-30 hidden lg:block">
                     <div className="w-6 h-6 bg-white dark:bg-[#161616] border border-gray-200 dark:border-[#393939] rounded-full flex items-center justify-center text-gray-400">
                        <ArrowRight size={12} />
                     </div>
                  </div>
                  <div className="absolute top-1/2 right-0 translate-x-1/2 -translate-y-1/2 z-30 hidden lg:block">
                     <div className="w-6 h-6 bg-white dark:bg-[#161616] border border-gray-200 dark:border-[#393939] rounded-full flex items-center justify-center text-gray-400">
                        <ArrowRight size={12} />
                     </div>
                  </div>
               </div>

               {/* Zone 3: Outbound (Right) */}
               <div className="flex-1 p-8 bg-white dark:bg-[#161616]">
                  <div className="flex items-center gap-3 mb-6">
                     <div className="w-8 h-8 rounded-full text-white flex items-center justify-center font-bold text-sm shadow-md bg-[#474D22]">2</div>
                     <div>
                        <h3 className="text-sm font-bold text-[#161616] dark:text-white uppercase tracking-wider">{currentConfig.outboundLabel}</h3>
                        <p className="text-[10px] text-gray-500 dark:text-gray-400 font-medium uppercase tracking-widest">Destinations</p>
                     </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                     {currentConfig.outbound.map((conn) => (
                        <OutboundTile 
                          key={conn.type} 
                          type={conn.type} 
                          isActive={conn.isActive} 
                          isInternal={false}
                        />
                     ))}
                  </div>
               </div>

            </div>
          </Card>
        </div>

        {/* Right Column: TXE Usage */}
        <div className="xl:col-span-3">
          <Card overflowHidden className="p-8 h-full flex flex-col justify-between bg-white dark:bg-[#161616] border border-gray-200 dark:border-[#393939]">
            <div>
              <div className="flex justify-between items-center mb-10">
                 <h3 className="text-lg font-bold tracking-wider text-[#161616] dark:text-white uppercase">{t('txe_usage')}</h3>
                 <div className="p-2 bg-gray-100 dark:bg-white/10 rounded-full">
                    <TrendingUp size={20} className="text-[#474D22] dark:text-white" />
                 </div>
              </div>
              <div className="space-y-6">
                 <div>
                    <div className="flex justify-between items-end mb-3">
                       <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">{t('used_available')}</span>
                       <div className="text-2xl font-light text-[#161616] dark:text-white">
                         {usageData.txe.used.toLocaleString()} 
                         <span className="text-sm font-normal text-gray-400 dark:text-gray-500 ml-1">/ {usageData.txe.total.toLocaleString()}</span>
                       </div>
                    </div>
                    <div className="h-1.5 w-full bg-gray-200 dark:bg-[#393939] rounded-full overflow-hidden">
                       <div className="h-full bg-[#474D22]" style={{ width: `${usagePercent}%` }} />
                    </div>
                 </div>
                 
                 <div className="p-4 bg-gray-50 dark:bg-white/5 rounded-[6px] border border-gray-100 dark:border-white/5 mt-6">
                    <div className="flex items-center gap-3 mb-2">
                       {/* <Server size={14} className="text-gray-400" /> */}
                       <span className="text-[10px] font-bold text-gray-500 dark:text-gray-300 uppercase tracking-widest">Current Plan</span>
                    </div>
                    <p className="text-sm font-bold text-[#161616] dark:text-white">Enterprise Volume</p>
                 </div>
              </div>
            </div>
            <div className="flex flex-col gap-3 mt-8">
               <Button variant="outline" className="w-full border-gray-300 dark:border-gray-600 text-brand-dark dark:text-white hover:bg-gray-50 dark:hover:bg-white/10 rounded-[6px]">{t('details')}</Button>
            </div>
          </Card>
        </div>
      </div>

      <div className="mt-6">
        <FlowDiagram 
          period={selectedPeriod} 
          onPeriodChange={setSelectedPeriod} 
          customNodes={reactiveTopology} 
          customIngestionValue={primaryIngestionValue}
          filters={{ company, view: 'debitor', status, dateRange }}
          onFilterChange={{
            setCompany,
            setView: () => {}, // No-op
            setStatus,
            setDateRange
          }}
        />
      </div>
    </div>
  );
};

export default Dashboard;
